// ════════════════════════════════════════════════════════════════════
// A/B TESTING UI PILOTÉ PAR LES AGENTS (remote-config).
//
// Les agents acquisition / rétention-UX créent des expériences sur des
// SURFACES whitelistées (textes uniquement : titres, sous-titres, CTA).
// Ici, côté client :
//   1. fetch des expériences actives (cache localStorage 5 min → pas de
//      flicker au 2e chargement, 1 fetch léger au 1er)
//   2. assignation DÉTERMINISTE par visiteur : hash(anon_id + exp.id) % 100
//      → sticky sans stockage, respecte l'allocation ({A:50,B:50})
//   3. getSlot(surface, fallback) : le texte à afficher (fallback = le
//      texte codé en dur, servi si pas d'expérience ou fetch KO)
//   4. getAssignments() : { surface: variante } attaché à CHAQUE event
//      (trackStudioFunnel) → la mesure par variante est automatique
// Une expérience "decided" avec un gagnant est servie à 100 % (rollout).
// ════════════════════════════════════════════════════════════════════
import { API_URL } from './config'

const CACHE_KEY = 'studio_ui_experiments'
const CACHE_TTL = 5 * 60 * 1000
const ANON_ID_KEY = 'studio_anon_id' // même clé que metaPixel.getAnonId

let experiments = []   // [{id, surface, variants, allocation, status, winner}]
let loaded = false
const listeners = new Set()

function anonId() {
  try { return localStorage.getItem(ANON_ID_KEY) || 'anon' } catch { return 'anon' }
}

// fnv1a : rapide, stable, suffisant pour un split 0-99.
function bucket(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) }
  return (h >>> 0) % 100
}

function variantFor(exp) {
  if (exp.status === 'decided' && exp.winner) return exp.winner
  const alloc = exp.allocation || { A: 50, B: 50 }
  const b = bucket(`${anonId()}:${exp.id}`)
  let acc = 0
  for (const v of (exp.variants || [])) {
    acc += Number(alloc[v.key] ?? 0)
    if (b < acc) return v.key
  }
  return (exp.variants?.[0] || {}).key || 'A'
}

function applyCache(list) {
  experiments = Array.isArray(list) ? list : []
  loaded = true
  for (const fn of listeners) { try { fn() } catch { /* noop */ } }
}

export function initUiExperiments() {
  // 1. cache local (anti-flicker)
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) {
      const { at, list } = JSON.parse(raw)
      if (Date.now() - at < CACHE_TTL) applyCache(list)
    }
  } catch { /* noop */ }
  // 2. fetch frais (met à jour le cache pour la prochaine fois)
  fetch(`${API_URL}/api/studio/ui-experiments`)
    .then(r => r.ok ? r.json() : Promise.reject())
    .then(d => {
      applyCache(d.experiments || [])
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), list: experiments })) } catch { /* noop */ }
    })
    .catch(() => { if (!loaded) applyCache([]) })
}

// Le texte à afficher pour une surface. config des variantes : soit une
// string, soit un objet ({before, highlight, after} pour les titres).
export function getSlot(surface, fallback) {
  const exp = experiments.find(e => e.surface === surface && (e.status === 'running' || (e.status === 'decided' && e.winner)))
  if (!exp) return fallback
  const key = variantFor(exp)
  const v = (exp.variants || []).find(x => x.key === key)
  const cfg = v?.config
  if (cfg == null) return fallback
  return typeof cfg === 'object' && !Array.isArray(cfg) ? { ...(typeof fallback === 'object' ? fallback : {}), ...cfg } : cfg
}

// { surface: variante } pour les expériences EN COURS uniquement (les
// rollouts ne se mesurent plus). Attaché à chaque event par metaPixel.
// La CLÉ de variante d'une surface (pour les variantes STRUCTURELLES,
// pas seulement du texte — ex: le mur de la note A/B). null si pas
// d'expérience active sur cette surface.
export function getVariantKey(surface) {
  const exp = experiments.find(e => e.surface === surface && (e.status === 'running' || (e.status === 'decided' && e.winner)))
  return exp ? variantFor(exp) : null
}

export function getAssignments() {
  const out = {}
  for (const e of experiments) {
    if (e.status !== 'running') continue
    out[e.surface] = variantFor(e)
  }
  return out
}

// true dès que les expériences sont connues (cache local OU fetch OU échec).
// Permet aux surfaces STRUCTURELLES (mur de la note) de ne pas peindre la
// mauvaise variante pendant le fetch (flash de la note en clair ~0,5s).
export function isUiExpLoaded() {
  return loaded
}

// Abonnement React minimal : re-render quand les expériences arrivent.
export function subscribeUiExperiments(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
